import _ from 'underscore';

import { getValue } from 'utils/collections';
import { clamp } from 'utils/numbers';
import { rampToGainAtTime, setGainAtTime } from './common';
import {
  scheduleGainChanges,
  shouldUseImmediateTransition,
} from './gain-mixer';
import { IAudioLevel, IAudioSource, IWebAudioSource } from './types';

interface ISources {
  [id: string]: IWebAudioSource;
}

/**
 * Time coordinate systems to understand for this class:
 *  - audioContext: the audio context time is always increasing and is used to
 *    track and schedule audio events.  It starts at 0 when it's initialized and
 *    continues to increase as time passes.
 *
 *  - audio or "global" time: this is the time coordinate system that the outside
 *    world sees, where 0 is the beginning of the audio and its duration is the end.
 *    the audio might be made of multiple sources, each with their own segments and
 *    "windows" of time.  This is the highest level abstraction over the audio graph
 *
 *  - "window" or "source-window": each source has a window of time that is being
 *    played, defined by the source's configuration.  It is the intersection of
 *    the source's segments and the audio context's time coordinate system.
 *
 *  - source: each underlying audio source has its own time coordinate system,
 *    where the beginning of the source is 0 and the end of the source is its
 *    duration
 *
 * Layout of the WebAudio Graph:
 *  Each source represents a single audio asset on this player's timeline.  The
 *  source can be made up of multiple clips or segments, each with its own audio
 *  source node. Conceptually, each source node is connected end-to-end to create
 *  the underlying audio source, from which a clip is played during playback.  Each
 *  source might also have its own fade transitions. Furthermore, there is a
 *  master volume control that has to be accounted for.
 *
 *  For each source, we create a gain bus, which is a ConstantSourceNode.  This
 *  node is used for scheduling fade transitions, adjusting the volume of the
 *  source as the master gain changes, and "broadcasting" the source's gain value
 *  to the underlying segment buffers.
 *
 *  Each segment buffer has its own gain node, whose value is controlled by the
 *  gain bus.  The segment buffer is then connected to the gain node and the gain
 *  node to the destination.
 */
export default class BaseAudio {
  protected audioCtx: AudioContext;
  /**
   * duration in the "global" time coordinate system.  this cannot be calculated
   * from sources or configurations because there might be silence at the beginning
   * or end that the sources are unaware of
   */
  protected durationSeconds: number;
  /**
   * this is duration of audio that has elapsed when playback is paused
   */
  protected pausedAt: number;
  protected playing: boolean;
  /**
   * this is a value in the "audioContext" time coordinate system indicating when
   * playback was started.  This is the "origin" when scheduling audio events
   */
  protected startedAt: number;
  protected sources: ISources = {};
  protected masterGain: number;

  constructor(
    ctx: AudioContext,
    durationSeconds: number,
    sources: IAudioSource[],
  ) {
    this.masterGain = 1;
    this.audioCtx = ctx;
    this.sources = sources.reduce((acc, source) => {
      acc[source.id] = source;
      acc[source.id].gainBus = this.createGainBus();
      acc[source.id].gainNodes = [];
      return acc;
    }, {} as ISources);

    this.durationSeconds = durationSeconds;
    this.resetPlayTrackingVariables();
  }

  private createGainBus() {
    const node = this.audioCtx.createConstantSource();
    node.start();
    return node;
  }

  private resetPlayTrackingVariables() {
    this.startedAt = 0;
    this.pausedAt = 0;
    this.playing = false;
  }

  private connectNodeToAudioGraph(
    node: AudioBufferSourceNode,
    index: number,
    source: IWebAudioSource,
  ) {
    if (source.gainBus) {
      const gainNode = this.getSourceBufferGainNode(source.id, index);
      source.gainBus.connect(gainNode.gain);
      node.connect(gainNode);
      gainNode.connect(this.audioCtx.destination);
    } else {
      node.connect(this.audioCtx.destination);
    }
  }

  /**
   *
   * @param source the audio source to which the buffer belongs
   * @param playheadSec the current playback position
   * @param bufferIndex the index of the buffer being scheduled
   * @param elapsedBufferSeconds when scheduling nodes, this is the sum duration
   *   of all of the buffers that have already been scheduled
   */
  private calculateBufferTiming(
    source: IWebAudioSource,
    playheadSec: number,
    bufferIndex: number,
    elapsedBufferSeconds: number,
  ) {
    const buffer = source.buffers[bufferIndex];

    // the entire duration of what is being played for this source, regadless
    // of how that playback is constructed.
    const sourceDurationSeconds = source.toSeconds - source.fromSeconds;

    // where playback should end on the "source" time coordinate system
    const { toSeconds } = source;

    // this is a value in the "window" time coordinate system indicating where
    // playback should start. for example, a source could have a startOffsetMilli
    // of 1000ms and a source duration of 5000ms.  The "window" time coordinate
    // system is a timeline spanning 0 to 4000ms
    const sourceWindowPlayhead = clamp(
      playheadSec - source.startOffsetSeconds,
      0,
      sourceDurationSeconds,
    );

    // this is the a value in the "source" time coordinate system indicating
    // where playback should start within the source
    const sourcePlayhead = source.fromSeconds + sourceWindowPlayhead;

    // `bufferTimelineStart` is this buffer's position in the "window" time
    // coordinate system — i.e. where it begins on the output playback timeline.
    // The source contributes `startOffsetSeconds` (where the source itself
    // begins in the window), plus the source-time distance from the source's
    // start to this buffer's start (`elapsedBufferSeconds - fromSeconds`),
    // since buffers are laid out contiguously after their source's origin.
    const bufferTimelineStart =
      source.startOffsetSeconds +
      Math.max(0, elapsedBufferSeconds - source.fromSeconds);

    // Convert the buffer's window-time start into a Web Audio clock time by
    // anchoring it to `currentTime`: `bufferTimelineStart - playheadSec` is how
    // far in the future (or past) this buffer's start is relative to where the
    // playhead currently sits, so adding `currentTime` yields the absolute
    // AudioContext time at which to schedule it. The max with `currentTime`
    // clamps any result that falls in the past up to "now" — Web Audio would
    // otherwise start those nodes immediately, and we want that to be explicit.
    const startWhen = Math.max(
      this.audioCtx.currentTime,
      this.audioCtx.currentTime + bufferTimelineStart - playheadSec,
    );

    //  this maps the source's playhead position to the buffer's time coordinate
    // system.  If the the playhead is before or after the segment, it is clamped
    // to the respective boundary
    const startOffset = clamp(
      sourcePlayhead - elapsedBufferSeconds,
      0,
      buffer.duration,
    );

    const duration = Math.max(
      0,
      // this calculates where the buffer would end, but clamps it to the clip's
      // end boundary. this is a value in the "source" time coordinate system.
      // this value is the end position of the buffer's playback
      Math.min(elapsedBufferSeconds + buffer.duration, toSeconds) -
        // this calculates when the segment should start playing, but clamps it
        // to the segment's start boundary
        Math.max(elapsedBufferSeconds, sourcePlayhead),
    );

    return { duration, startOffset, startWhen };
  }

  /**
   *
   * @param offsetSec tells playback where to start in the "global" time coordinate
   *        system.
   */
  protected startPlayback(offsetSec?: number) {
    if (this.playing) return;

    /*
     * https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
     *
     * NOTE we might have to revisit this.  the link above provides an example where execution
     * continues in resume().then().  the same link provides a PR as an example of how easy it is to
     * work with chrome's new autoplay rules and the code in the PR doesn't wait for the resume()
     * promise to resolve.
     */
    if (this.audioCtx.state === 'suspended') {
      this.audioCtx.resume();
    }

    // the amount of time that has elapsed since the beginning of the audio.  this
    // is a value in the "global" time coordinate system
    const playheadSec = offsetSec ?? this.pausedAt;

    /**
     * where playback begins, or if it's not starting from the beginning, then
     * where it began.
     *
     * when playback starts from the beginning, this.pausedAt is 0 and so the
     * audio context's current time is where playback begins.
     *
     * when playback starts at a non-zero offset from the beginning, then we have to
     * record when playback began if it had started at zero, which involves subtracing
     * the current playback time from the audio context's time
     *
     */
    this.startedAt = this.audioCtx.currentTime - playheadSec;
    this.pausedAt = 0;
    this.playing = true;

    // it's possible to play audio that has no sound
    Object.keys(this.sources).forEach(key => {
      const source = this.sources[key];

      this.rescheduleGainChanges(source);

      // each source can have multiple segments.  the gain for the soure is handled
      // by a single node. rather than having to coordinate the gain across all of
      // the segments, the source's gain node is used to braodcast its value
      // to each segment using this constant source node
      // const gainBroadcaster = thi.createGainBroadcaster(source);

      // when scheduling nodes, this is the sum duration of all of the buffers
      // that have already been scheduled
      let elapsedBufferSeconds = 0;

      source.nodes = source.buffers.map((buffer, index) => {
        const node = this.audioCtx.createBufferSource();
        node.buffer = buffer;

        this.connectNodeToAudioGraph(node, index, source);

        const { duration, startOffset, startWhen } = this.calculateBufferTiming(
          source,
          playheadSec,
          index,
          elapsedBufferSeconds,
        );

        node.start(startWhen, startOffset, duration);

        elapsedBufferSeconds += buffer.duration;

        return node;
      });
    });
  }

  private stopSource(source: IWebAudioSource) {
    // each node is connected to its own gain node.  the gain node's value is controlled
    // by a single ConstantSourceNode for the source.  All of this should be disconnected.
    source.nodes?.forEach((node, index) => {
      // the buffer's gain node, which is connected to the source's gain bus
      // (constant node)
      const gainNode = this.getSourceBufferGainNode(source.id, index);

      try {
        node.stop(0);
      } catch {
        // ignore
      }

      // standard stop/cleanup procedure.  setting onended to nul prevents callbacks
      // and closures that could prevent the node from being garbage collected
      node.onended = null;
      node.disconnect();
      node.buffer = null;

      // disconnect the gain node
      if (gainNode) {
        // if there's a bus, disconnect the bus. we'll reuse the bus though - no
        // reason to recreate it
        if (source.gainBus) {
          source.gainBus.disconnect(gainNode.gain);
        }

        // this is just a best practice - don't really schedule any events on the
        // per-buffer gain nodes.  everything is scheduled on the gain bus
        gainNode.gain.cancelScheduledValues(0);
        gainNode.disconnect();
      }
    });

    // cancel any scheduled changes on the gain bus
    this.clearScheduledGainChanges(source.id);

    // clear out the source state
    source.nodes = [];
    source.gainNodes = [];
  }

  protected stopPlayback() {
    Object.keys(this.sources).forEach(key => {
      const source = this.sources[key];
      this.stopSource(source);
    });

    this.resetPlayTrackingVariables();
  }

  protected pausePlayback() {
    const elapsed = this.audioCtx.currentTime - this.startedAt;
    this.stopPlayback();
    this.pausedAt = elapsed;
  }

  private seekWhilePaused(sec: number) {
    this.pausedAt = sec;
  }

  private seekWhilePlaying(sec: number) {
    this.pausePlayback();
    this.seekWhilePaused(sec);
    if (sec < this.durationSeconds) {
      this.startPlayback();
    }
  }

  protected seekTo(sec: number) {
    if (this.playing) {
      this.seekWhilePlaying(sec);
    } else {
      this.seekWhilePaused(sec);
    }
  }

  protected get currentTime() {
    if (this.pausedAt) {
      return this.pausedAt;
    }

    if (this.startedAt) {
      return this.audioCtx.currentTime - this.startedAt;
    }

    return 0;
  }

  private getSourceGainBus(id: string) {
    const source = this.sources[id];

    if (source.gainBus) {
      return source.gainBus;
    }

    const gainNode = this.createGainBus();
    source.gainBus = gainNode;
    return gainNode;
  }

  private getSourceBufferGainNode(id: string, index: number) {
    const source = this.sources[id];
    const node = source?.gainNodes?.[index];

    if (node) {
      return node;
    }

    const gainNode = this.audioCtx.createGain();
    gainNode.gain.value = 0;
    source.gainNodes[index] = gainNode;
    return gainNode;
  }

  protected setMasterGain(val: number) {
    this.masterGain = val;
    Object.keys(this.sources).forEach(id => {
      const gainNode = this.getSourceGainBus(id);
      const newVal = gainNode.offset.value * this.masterGain;

      setGainAtTime(gainNode.offset, newVal, this.audioCtx.currentTime);

      this.rescheduleGainChanges(this.sources[id]);
    });
  }

  private clearScheduledGainChanges(id: string) {
    const { gainBus: gainNode } = this.sources[id];
    gainNode && gainNode.offset.cancelScheduledValues(0);
  }

  private scheduleGainChanges(
    source: IWebAudioSource,
    contextSec: number = this.startedAt,
  ) {
    scheduleGainChanges(source, transition => {
      const gainNode = this.getSourceGainBus(source.id);

      /*
       * don't schedule this transition if startSec is in the past.
       *
       * anytime the playhead stops (or seeks while pause) we record the time.
       * so if you seek to 5sec, this.pausedAt === 5.
       *
       * if you seek to 5sec before the audioContext.currentTime reaches 5sec,
       * then startSec below will be negative.
       */
      const startSec =
        transition.atSec + source.startOffsetSeconds + contextSec;

      switch (transition.effect) {
        case 'cut':
          setGainAtTime(
            gainNode.offset,
            transition.toGain * this.masterGain,
            startSec,
            shouldUseImmediateTransition(transition),
          );
          break;

        case 'fade': {
          const { fromGain, toGain, durationSec } = transition;
          rampToGainAtTime(
            gainNode.offset,
            fromGain * this.masterGain,
            toGain * this.masterGain,
            startSec,
            durationSec,
          );
          break;
        }
      }
    });
  }

  private rescheduleGainChanges(source: IWebAudioSource, contextSec?: number) {
    this.clearScheduledGainChanges(source.id);
    this.scheduleGainChanges(source, contextSec);
  }

  private updateSourceTiming(
    id: string,
    prop: 'startOffsetSeconds' | 'fromSeconds' | 'toSeconds',
    value: number,
  ) {
    if (_.isUndefined(value)) return;
    const source = this.sources[id];
    source[prop] = value;

    if (this.playing) {
      this.pausePlayback();
      this.startPlayback();
    }
  }

  public setSourceAudioOffsetSeconds(id: string, offsetSeconds: number) {
    this.updateSourceTiming(id, 'startOffsetSeconds', offsetSeconds);
  }

  public setSourceFromSeconds(id: string, fromSeconds: number) {
    this.updateSourceTiming(id, 'fromSeconds', fromSeconds);
  }

  public setSourceToSeconds(id: string, toSeconds: number) {
    this.updateSourceTiming(id, 'toSeconds', toSeconds);
  }

  public setAudioLevels(id: string, audioLevels: IAudioLevel[]) {
    const source = this.sources[id];
    if (!source || !audioLevels) return;

    source.audioLevels = audioLevels;
    this.rescheduleGainChanges(source);
  }

  public getAudioLevels(id: string): IAudioLevel[] {
    return getValue(this.sources, [id, 'audioLevels']);
  }

  public getSourceAudioOffset(id: string): number {
    return getValue(this.sources, [id, 'startOffsetSeconds']);
  }

  public getSourceFromSeconds(id: string): number {
    return getValue(this.sources, [id, 'fromSeconds']);
  }

  public getSourceToSeconds(id: string): number {
    return getValue(this.sources, [id, 'toSeconds']);
  }

  public get isPlaying() {
    return this.playing;
  }

  public get duration() {
    return this.durationSeconds;
  }

  public set duration(seconds) {
    this.durationSeconds = seconds;
  }

  public get sourceIds() {
    return Object.keys(this.sources);
  }
}
